import { useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
} from '@mui/material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { enqueueSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { getPostLoginRedirectPath } from '../marketplace/didarSettingRoute';

// OTP Input Component (copied from Auth.tsx)
interface OTPInputProps {
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    error: boolean;
    helperText?: string;
    disabled?: boolean;
    shouldFocus?: boolean;
    onAutoSubmit?: () => void;
    onClearAndFocus?: (clearAndFocus: () => void) => void;
}

const OTPInput: React.FC<OTPInputProps> = ({ value, onChange, onBlur, error, helperText, disabled, shouldFocus, onAutoSubmit, onClearAndFocus }) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    
    // Create array with proper order - right to left (reverse order)
    const otpValues = Array(6).fill('');
    for (let i = 0; i < Math.min(value.length, 6); i++) {
      // Map the value to the reversed array (right to left)
        const reversedIndex = 5 - i;
        otpValues[reversedIndex] = value[i] || '';
    }

  // Set initial focus on rightmost input (index 5) when component mounts or when shouldFocus changes
    useEffect(() => {
        if (shouldFocus && inputRefs.current[5] && !disabled) {
          // Small delay to ensure the component is fully rendered
            setTimeout(() => {
            inputRefs.current[5]?.focus();
            }, 100);
        }
    }, [shouldFocus, disabled]);

  // Function to clear all OTP boxes and focus on the first one
    const clearAndFocus = () => {
    onChange(''); // Clear the OTP value
    // Focus on the rightmost input (index 5) which is the first one in our right-to-left flow
    setTimeout(() => {
      inputRefs.current[5]?.focus();
    }, 100);
  };

  // Expose the clearAndFocus function to parent component
  useEffect(() => {
    if (onClearAndFocus) {
      onClearAndFocus(clearAndFocus);
    }
  }, [onClearAndFocus]);

  const handleInputChange = (displayIndex: number, inputValue: string) => {
    // Only allow single digit
    if (inputValue.length > 1) {
      inputValue = inputValue.slice(-1);
    }
    
    // Only allow digits
    if (!/^\d*$/.test(inputValue)) {
      return;
    }

    const newOtpValues = [...otpValues];
    newOtpValues[displayIndex] = inputValue;
    
    // Create the actual OTP string in correct order (left to right)
    const actualOtpArray = Array(6).fill('');
    for (let i = 0; i < 6; i++) {
      actualOtpArray[i] = newOtpValues[5 - i];
    }
    const newOtpCode = actualOtpArray.join('');
    
    onChange(newOtpCode);

    // Auto submit when all 6 digits are entered
    if (newOtpCode.length === 6 && onAutoSubmit) {
      // Small delay to ensure the UI updates before submitting
      setTimeout(() => {
        onAutoSubmit();
      }, 100);
    }

    // Auto focus to next input (left direction)
    if (inputValue && displayIndex > 0) {
      inputRefs.current[displayIndex - 1]?.focus();
    }
  };

  const handleKeyDown = (displayIndex: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!otpValues[displayIndex] && displayIndex < 5) {
        // If current box is empty, move to next box (right direction)
        inputRefs.current[displayIndex + 1]?.focus();
      } else if (otpValues[displayIndex]) {
        // If current box has value, clear it
        const newOtpValues = [...otpValues];
        newOtpValues[displayIndex] = '';
        
        // Create the actual OTP string in correct order (left to right)
        const actualOtpArray = Array(6).fill('');
        for (let i = 0; i < 6; i++) {
          actualOtpArray[i] = newOtpValues[5 - i];
        }
        const newOtpCode = actualOtpArray.join('');
        onChange(newOtpCode);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length > 0) {
      onChange(pastedData);
      
      // Auto submit when all 6 digits are pasted
      if (pastedData.length === 6 && onAutoSubmit) {
        // Small delay to ensure the UI updates before submitting
        setTimeout(() => {
          onAutoSubmit();
        }, 100);
      }
      
      // Focus the rightmost empty input or the rightmost input
      const nextIndex = Math.max(5 - pastedData.length + 1, 0);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleBlur = () => {
    onBlur();
  };

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, justifyContent: 'center', mb: 1 }}>
        {Array.from({ length: 6 }, (_, index) => (
          <TextField
            key={index}
            inputRef={(el) => (inputRefs.current[index] = el)}
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              style: {
                textAlign: 'center',
                fontWeight: 'bold',
              },
            }}
            value={otpValues[index]}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onBlur={handleBlur}
            error={error}
            disabled={disabled}
            sx={{
              width: { xs: '40px', sm: '50px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
              '& .MuiInputBase-input': {
                fontSize: { xs: '1.2rem', sm: '1.5rem' },
                padding: { xs: '8px', sm: '12px' },
              },
            }}
          />
        ))}
      </Box>
      {error && helperText && (
        <Typography variant="caption" color="error" sx={{ textAlign: 'center', display: 'block', mt: 1 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

interface TwoFAForm {
  phone_number: string;
  two_factor_auth_code: string;
}

const TwoFA: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { loginWithTwoFactor } = useAuth();
  const clearOtpAndFocusRef = useRef<(() => void) | null>(null);

  const formik = useFormik<TwoFAForm>({
    initialValues: {
      phone_number: '',
      two_factor_auth_code: '',
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate: (values) => {
      const errors: any = {};
      
      // Validate phone number
      if (!values.phone_number || values.phone_number.length === 0) {
        errors.phone_number = 'شماره تلفن همراه خود را وارد کنید';
      } else {
        const phoneRegex = /^\+?[0-9۰-۹]+$/;
        if (!phoneRegex.test(values.phone_number)) {
          errors.phone_number = 'شماره تلفن همراه نامعتبر است';
        }
      }
      
      // Validate OTP
      if (!values.two_factor_auth_code) {
        errors.two_factor_auth_code = 'کد تایید را وارد کنید';
      } else if (values.two_factor_auth_code.length !== 6) {
        errors.two_factor_auth_code = 'کد تایید باید 6 رقم باشد';
      }
      
      return errors;
    },
    onSubmit: async (values) => {
      try {
        const authenticatedUser = await loginWithTwoFactor(
          values.phone_number,
          values.two_factor_auth_code,
        );
        navigate(
          getPostLoginRedirectPath(searchParams, location, authenticatedUser?.membership),
          { replace: true },
        );
      } catch (err: any) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        
        // Handle error cases
        if (status === 422) {
          enqueueSnackbar('اطلاعات وارد شده صحیح نیست', { 
            variant: 'error',
            autoHideDuration: 4000,
          });
        } else if (status === 429) {
          enqueueSnackbar('درخواست‌های زیادی ارسال کرده‌اید. لطفاً کمی صبر کنید.', { 
            variant: 'error',
            autoHideDuration: 5000,
          });
        } else if (status === 500) {
        let message = 'خطای سرور. لطفاً دوباره تلاش کنید';
          if (detail && detail.includes("Invalid phone number format")) {
            message = 'شماره تلفن همراه نامعتبر است. لطفاً شماره صحیح را وارد کنید';
          } else if (detail && detail.includes("کد تایید نامعتبر است") || detail && detail.includes("Invalid OTP")) {
            message = 'کد تایید نامعتبر است. لطفاً کد صحیح را وارد کنید';
            // Clear OTP and focus on first input when OTP is invalid
            if (clearOtpAndFocusRef.current) {
              clearOtpAndFocusRef.current();
            }
          } else {
            message = detail || 'خطای سرور. لطفاً دوباره تلاش کنید';
          }
          enqueueSnackbar(message, { 
            variant: 'error',
            autoHideDuration: 4000,
          });
        } else if (status === 503) {
          enqueueSnackbar('سرویس در دسترس نیست. لطفاً بعداً تلاش کنید', { 
            variant: 'error',
            autoHideDuration: 4000,
          });
        } else if (status === 0 || !status) {
          enqueueSnackbar('خطای شبکه. لطفاً اتصال اینترنت خود را بررسی کنید', { 
            variant: 'error',
            autoHideDuration: 4000,
          });
        } else {
          enqueueSnackbar(detail || 'خطایی رخ داده است. لطفاً دوباره تلاش کنید', { 
            variant: 'error',
            autoHideDuration: 4000,
          });
        }
      }
    },
  });

  return (
    <Box
      sx={{
        background: 'radial-gradient(circle,rgba(255, 255, 255, 1) 0%, rgba(0, 198, 179, 0.66) 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 450,
          mx: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 3,
          alignItems: 'center',
        }}
      >
        <img src="/images/aida-logo-ok.svg" alt="" width={100} style={{marginBottom: 10}}/>
        <Typography variant="h5" component="h1" gutterBottom align="center">
          احراز هویت
        </Typography>

        <form onSubmit={formik.handleSubmit} style={{maxWidth: '386px', width: '100%'}}>
          {/* Phone Number Field */}
          <TextField
            fullWidth
            id="phone_number"
            name="phone_number"
            label="شماره تلفن همراه"
            value={formik.values.phone_number}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phone_number && Boolean(formik.errors.phone_number)}
            helperText={formik.touched.phone_number && formik.errors.phone_number}
            margin="normal"
            dir="ltr"
            placeholder="شماره تلفن همراه خود را وارد کنید"
          />

          {/* OTP Field */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
              کد تایید 6 رقمی
            </Typography>
            <OTPInput
              value={formik.values.two_factor_auth_code}
              onChange={(value) => formik.setFieldValue('two_factor_auth_code', value)}
              onBlur={() => formik.setFieldTouched('two_factor_auth_code', true)}
              error={Boolean(formik.touched.two_factor_auth_code && formik.errors.two_factor_auth_code)}
              helperText={formik.touched.two_factor_auth_code && formik.errors.two_factor_auth_code ? String(formik.errors.two_factor_auth_code) : undefined}
              shouldFocus={true}
              onAutoSubmit={() => formik.handleSubmit()}
              onClearAndFocus={(clearAndFocus) => {
                clearOtpAndFocusRef.current = clearAndFocus;
              }}
            />
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default TwoFA;
